import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useMemo } from 'react';

import { Button } from '@/components/common/button/Button';
import { Flex } from '@/components/common/Flex';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { Stepper } from '@/components/common/Stepper';
import { Text } from '@/components/common/Text';
import { richComponents } from '@/constants/rich/richComponents';
import { type StepParams } from '@/hooks/use-stepper';

import styles from './StepLayout.module.scss';

export type StepLayoutProps = {
  stepParams: StepParams;
  renderForm: (props: { buttons: ReactNode }) => ReactNode;
  goToStep0: () => unknown;
};

export const StepLayout: FC<StepLayoutProps> = (props) => {
  const t = useTranslations('pages.account.login.form.setup2FaBlock.steps.setup');

  const {
    stepParams: { currentStep, stepsCount, isLastStep, goToPrevStep },
    renderForm,
    goToStep0,
  } = props;

  const currentStepKey = (['scan', 'code'] as const).at(currentStep) ?? 'scan';
  const tStep = useTranslations(`pages.account.login.form.setup2FaBlock.steps.setup.steps.${currentStepKey}`);

  const formButtons = useMemo(() => {
    return (
      <Flex direction="column" gap={4}>
        <FormSubmitButton>{isLastStep ? t('buttons.complete') : t('buttons.next')}</FormSubmitButton>

        <Button variant="secondary" onClick={currentStep === 0 ? goToStep0 : goToPrevStep}>
          {t('buttons.back')}
        </Button>
      </Flex>
    );
  }, [currentStep, goToPrevStep, goToStep0, isLastStep, t]);

  const form = useMemo(() => {
    return renderForm({
      buttons: formButtons,
    });
  }, [formButtons, renderForm]);

  return (
    <div className={styles.root}>
      <Text className={styles.title} type="medium27">
        {t('title')}
      </Text>

      <Flex className={styles.form} direction="column">
        <Flex className={styles.stepTitle} alignItems="center" justifyContent="spaceBetween">
          <Text type="medium18">{tStep('title')}</Text>

          <Text type="medium18">
            {currentStep + 1}/{stepsCount}
          </Text>
        </Flex>

        {form}
      </Flex>

      <Stepper className={styles.stepper} currentStep={currentStep} stepsCount={stepsCount} />

      <Flex className={styles.content} direction="column" gap={4}>
        <Text type="medium48">{tStep('title')}</Text>

        <Text type="regular18" color="white-50p">
          {tStep.rich('content.subtitle', richComponents)}
        </Text>
      </Flex>
    </div>
  );
};
